import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useT } from "@/i18n";

// Home chapters, in page order. Section labels inside a chapter are numbered by CSS counters (App.css: .chapter).
export const CHAPTERS = [
  ["presentazione", "Presentazione"],
  ["prodotti", "Prodotti"],
  ["case", "Case in legno"],
  ["industria", "L'industria"],
  ["chi-siamo", "Chi siamo"],
  ["balneari", "Stabilimenti balneari"],
  ["progetti", "Progetti e cantieri"],
  ["qualita", "Qualità e metodo"],
  ["domande", "Domande"],
  ["contatti", "Contatti e sedi"],
];

const num = (i) => String(i + 1).padStart(2, "0");

// One chapter of the home page with its caption strip ("02 — Chi siamo").
export const Chapter = ({ n, caption = true, children }) => {
  const t = useT();
  const [id, label] = CHAPTERS[n - 1];
  return (
    <div id={`cap-${id}`} className="chapter" data-chapter={n} data-testid={`chapter-${id}`}>
      {caption && (
        <div className="bg-[#F7F5F0] px-5 pt-8 text-[#121110] sm:px-8" data-testid={`chapter-caption-${id}`}>
          <div className="mx-auto flex max-w-[1500px] items-center gap-3 border-b border-[#121110]/10 pb-2 font-mono text-xs uppercase tracking-[0.25em] text-[#6E685E]">
            <span className="text-[#C26D38]">{num(n - 1)}</span>
            <span aria-hidden="true">—</span>
            <span>{t(`chap.${id}`, label)}</span>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

const useActiveChapter = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const els = CHAPTERS.map(([id]) => document.getElementById(`cap-${id}`)).filter(Boolean);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.chapter) - 1); });
    }, { rootMargin: "-45% 0px -50% 0px" });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return active;
};

const go = (e, id, after) => {
  e.preventDefault();
  scrollToId(`#cap-${id}`);
  if (after) after();
};

// Fixed chapter index: vertical list on the right (desktop), compact pill with a pop-up list (phones).
export const ChapterIndex = () => {
  const t = useT();
  const active = useActiveChapter();
  const [open, setOpen] = useState(false);
  const shown = active > 0;
  return (
    <>
      <nav aria-label={t("chap.index", "Indice dei capitoli")} className={`fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 transition-opacity duration-500 min-[1640px]:block ${shown ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid="chapter-index">
        <ol className="flex flex-col gap-1 rounded-full border border-[#121110]/10 bg-[#F7F5F0]/85 px-2 py-3 shadow-[0_10px_30px_-15px_rgba(18,17,16,0.35)] backdrop-blur-md">
          {CHAPTERS.map(([id, label], i) => (
            <li key={id}>
              <a href={`#cap-${id}`} onClick={(e) => go(e, id)} aria-current={i === active ? "true" : undefined}
                className="group relative flex h-8 w-8 items-center justify-center rounded-full font-mono text-xs transition-[background-color,color] duration-300"
                data-testid={`chapter-index-${id}`}>
                <span className={`grid h-8 w-8 place-items-center rounded-full ${i === active ? "bg-[#C26D38] text-[#121110]" : "text-[#6E685E] group-hover:text-[#121110]"}`}>{num(i)}</span>
                <span className="pointer-events-none absolute right-11 whitespace-nowrap rounded-full bg-[#121110] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-[#F7F5F0] opacity-0 transition-[opacity,transform] duration-300 group-hover:-translate-x-1 group-hover:opacity-100 keep-dark">
                  {t(`chap.${id}`, label)}
                </span>
              </a>
            </li>
          ))}
        </ol>
      </nav>
      <div className={`fixed bottom-5 left-4 z-40 transition-opacity duration-500 min-[1640px]:hidden ${shown ? "opacity-100" : "pointer-events-none opacity-0"}`} data-testid="chapter-index-mobile">
        {open && (
          <ol className="mb-2 max-h-[60vh] overflow-y-auto rounded-3xl border border-[#121110]/10 bg-[#F7F5F0]/95 p-2 shadow-xl backdrop-blur-md" data-testid="chapter-index-mobile-list">
            {CHAPTERS.map(([id, label], i) => (
              <li key={id}>
                <a href={`#cap-${id}`} onClick={(e) => go(e, id, () => setOpen(false))}
                  className={`flex items-center gap-3 rounded-full px-4 py-2.5 text-sm ${i === active ? "bg-[#C26D38] text-[#121110]" : "text-[#121110]"}`}
                  data-testid={`chapter-index-mobile-${id}`}>
                  <span className="font-mono text-xs">{num(i)}</span> {t(`chap.${id}`, label)}
                </a>
              </li>
            ))}
          </ol>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="flex items-center gap-2 rounded-full border border-[#121110]/10 bg-[#F7F5F0]/90 px-4 py-3 text-sm text-[#121110] shadow-lg backdrop-blur-md"
          data-testid="chapter-index-mobile-toggle">
          <span className="font-mono text-xs text-[#C26D38]">{num(active)}</span>
          <span className="max-w-[46vw] truncate font-semibold">{t(`chap.${CHAPTERS[active][0]}`, CHAPTERS[active][1])}</span>
          <ChevronUp className={`h-4 w-4 transition-transform duration-300 ${open ? "" : "rotate-180"}`} />
        </button>
      </div>
    </>
  );
};
